import React, { useCallback } from "react";
import { FieldInputProps, FormikErrors, FormikProps, withFormik } from "formik";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  HStack,
  VStack,
} from "@chakra-ui/react";
import { CherryPayApi } from "../api/models";
import { isEmptyStr } from "../util/StringUtil";
import { TextField } from "../components/fields/TextField/TextField";
import { TextArea } from "../components/fields/TextArea/TextArea";
import { FieldWrapper } from "../components/fields/FieldWrapper/FieldWrapper";
import { FormStack } from "../components/FormStack/FormStack";
import { FormHelperText } from "@chakra-ui/form-control";

interface RoleRbacFormValues {
  displayName: string;
  description: string;
  permissions: string[];
}

interface RoleRbacFormProps {
  /**
   * The existing role if editing an existing role.
   */
  existingRole?: CherryPayApi.CherryHubRoleSummaryItem | null;
  /**
   * Permissions that can be assigned to a role.
   */
  availablePermissions: CherryPayApi.PermissionSummary[];
  /**
   * The product this role belongs to.
   */
  product: CherryPayApi.LicencedProduct;
  onCancel: () => void;
  onSave: (role: CherryPayApi.CherryHubRoleUpdate) => void;

  firstInputRef?: React.RefObject<HTMLInputElement>;
}

const RolePermissionsCheckboxGroup = ({
  disabled,
  field,
  form,
  availablePermissions,
}: {
  field: FieldInputProps<string[]>;
  form: FormikProps<any>;
  availablePermissions: CherryPayApi.PermissionSummary[];
  disabled?: boolean;
}) => {
  const onChange = useCallback(
    (val: string[]) => {
      if (disabled) {
        return;
      }
      form.setFieldValue(field.name, val);
    },
    [form.setFieldValue, field.name, disabled]
  );

  // Group permissions by GroupName
  const groupedPermissions = React.useMemo(() => {
    const groups: {
      [key: string]: {
        label: string;
        permissions: CherryPayApi.PermissionSummary[];
      };
    } = {};

    availablePermissions.forEach((permission) => {
      const groupName = permission.GroupName || "Other";
      if (!groups[groupName]) {
        groups[groupName] = {
          label: groupName,
          permissions: [],
        };
      }
      groups[groupName].permissions.push(permission);
    });

    return Object.values(groups);
  }, [availablePermissions]);

  return (
    <CheckboxGroup {...field} onChange={onChange}>
      <VStack w="100%">
        {availablePermissions.map(({ Id, DisplayName, Description }) => {
          return (
            <Checkbox key={Id} value={Id!!} w="100%" title={Description ?? ""}>
              {DisplayName}
            </Checkbox>
          );
        })}
      </VStack>
    </CheckboxGroup>
  );
};

const InnerForm = ({
  isSubmitting,
  isValid,
  onCancel,
  availablePermissions,
  dirty,
  submitForm,
}: RoleRbacFormProps & FormikProps<RoleRbacFormValues>) => {
  return (
    <FormStack>
      <TextField
        id="displayName-field"
        label="Name"
        name="displayName"
        placeholder="Enter role name"
      />
      <TextArea
        id="description-field"
        label="Description"
        name="description"
        placeholder="Enter role description"
      />
      <FieldWrapper name="permissions" label="Permissions">
        {({ field, form }) => (
          <>
            <FormHelperText mb="2">
              Choose which permissions should be assigned to this role.
            </FormHelperText>
            <RolePermissionsCheckboxGroup
              availablePermissions={availablePermissions}
              form={form}
              field={field}
            />
          </>
        )}
      </FieldWrapper>

      <HStack width="100%" justifyContent="end" spacing="3" pt="8">
        <Button
          colorScheme="cherryButton"
          disabled={isSubmitting || !isValid || !dirty}
          onClick={submitForm}
        >
          Save
        </Button>
        <Button disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </FormStack>
  );
};

export const RoleRbacForm = withFormik<RoleRbacFormProps, RoleRbacFormValues>({
  mapPropsToValues: (props) => {
    return {
      displayName: props.existingRole?.DisplayName ?? "",
      description: props.existingRole?.Description ?? "",
      permissions: props.existingRole?.Permissions ?? [],
    };
  },

  handleSubmit: async (values, { props }) => {
    props.onSave({
      DisplayName: values.displayName.trim(),
      Description: values.description.trim() || null,
      Product: props.product,
      Permissions: values.permissions,
    });
  },

  validateOnMount: false,

  validate: (values) => {
    let errors: FormikErrors<RoleRbacFormValues> = {};

    if (isEmptyStr(values.displayName)) {
      errors.displayName = "Name is required.";
    }

    if (values.permissions.length === 0) {
      errors.permissions = "A role must have at least one permission assigned.";
    }

    return errors;
  },
})(InnerForm);
